import { z } from "zod";
import type {
  bulkAssignManagerInputSchema,
  bulkChangeRoleInputSchema,
  bulkEditHrInputSchema,
  bulkInvitationActionInputSchema,
  bulkRemoveInputSchema,
  managerBulkEditPrefsInputSchema,
} from "@/features/people/schemas/bulk.schema";
import {
  type BulkActionResult,
  bulkActionResultSchema,
} from "@/features/people/schemas/bulk.schema";
import {
  type CreateDepartmentInput,
  type Department,
  departmentSchema,
  type ListDepartmentsResponse,
  listDepartmentsResponseSchema,
  type UpdateDepartmentInput,
} from "@/features/people/schemas/department.schema";
import {
  type DirectoryDetail,
  type DirectoryStats,
  directoryDetailSchema,
  directoryStatsSchema,
  type MyReportsResponse,
  myReportsResponseSchema,
} from "@/features/people/schemas/directory.schema";
import {
  type UpdateMembershipHrInput,
  type UpdateMembershipHrResponse,
  updateMembershipHrResponseSchema,
} from "@/features/people/schemas/hr.schema";
import {
  type CommitResult,
  commitResultSchema,
  type DryRunResult,
  dryRunResultSchema,
  type ImportHistoryList,
  importHistoryListSchema,
} from "@/features/people/schemas/import.schema";
import {
  type UpdateMembershipPrefsInput,
  type UpdateMembershipPrefsResponse,
  updateMembershipPrefsResponseSchema,
} from "@/features/people/schemas/prefs.schema";
import {
  type UpdateMemberProfileInput,
  type UpdateMemberProfileResponse,
  updateMemberProfileResponseSchema,
} from "@/features/people/schemas/profile.schema";
import {
  type ChangeMemberRoleInput,
  type ChangeMemberRoleResponse,
  changeMemberRoleResponseSchema,
} from "@/features/people/schemas/role.schema";
import { ApiError, apiCall as call } from "@/shared/lib/api-call";
import { apiPaths } from "@/shared/lib/api-paths";

// Re-exported for existing consumers of `@/features/people/people.api`'s
// `ApiError`. New code should import from `@/shared/lib/api-call`.
export { ApiError };

/// `listPeople` (and its toQueryString helper) moved to
/// `@/shared/membership/membership.api` in the C2 deepening, the
/// directory read is consumed by feedback, kudos, one-on-one, todos.

export function getPeopleStats(workspaceId: string): Promise<DirectoryStats> {
  return call(apiPaths.people.stats(workspaceId), { method: "GET" }, directoryStatsSchema);
}

export function getPersonDetail(
  workspaceId: string,
  membershipId: string,
): Promise<DirectoryDetail> {
  return call(
    apiPaths.people.detail(workspaceId, membershipId),
    { method: "GET" },
    directoryDetailSchema,
  );
}

export function updateMembershipPrefs(
  workspaceId: string,
  input: UpdateMembershipPrefsInput,
): Promise<UpdateMembershipPrefsResponse> {
  return call(
    apiPaths.memberships.mePrefs(workspaceId),
    { method: "PATCH", body: JSON.stringify(input) },
    updateMembershipPrefsResponseSchema,
  );
}

export function updateMembershipPrefsAdmin(
  workspaceId: string,
  membershipId: string,
  input: UpdateMembershipPrefsInput,
): Promise<UpdateMembershipPrefsResponse> {
  return call(
    apiPaths.memberships.prefs(workspaceId, membershipId),
    { method: "PATCH", body: JSON.stringify(input) },
    updateMembershipPrefsResponseSchema,
  );
}

export function updateMembershipHr(
  workspaceId: string,
  membershipId: string,
  input: UpdateMembershipHrInput,
): Promise<UpdateMembershipHrResponse> {
  return call(
    apiPaths.memberships.hr(workspaceId, membershipId),
    { method: "PATCH", body: JSON.stringify(input) },
    updateMembershipHrResponseSchema,
  );
}

export function updateMemberProfile(
  workspaceId: string,
  membershipId: string,
  input: UpdateMemberProfileInput,
): Promise<UpdateMemberProfileResponse> {
  return call(
    apiPaths.members.profile(workspaceId, membershipId),
    { method: "PATCH", body: JSON.stringify(input) },
    updateMemberProfileResponseSchema,
  );
}

export function changeMemberRole(
  workspaceId: string,
  membershipId: string,
  input: ChangeMemberRoleInput,
): Promise<ChangeMemberRoleResponse> {
  return call(
    apiPaths.members.member(workspaceId, membershipId),
    { method: "PATCH", body: JSON.stringify(input) },
    changeMemberRoleResponseSchema,
  );
}

// -- Departments -----------------------------------------------------------

export function listDepartments(
  workspaceId: string,
  opts: { includeDeleted?: boolean } = {},
): Promise<ListDepartmentsResponse> {
  const qs = opts.includeDeleted ? "?includeDeleted=true" : "";
  return call(
    `${apiPaths.departments.list(workspaceId)}${qs}`,
    { method: "GET" },
    listDepartmentsResponseSchema,
  );
}

export function createDepartment(
  workspaceId: string,
  input: CreateDepartmentInput,
): Promise<Department> {
  return call(
    apiPaths.departments.list(workspaceId),
    { method: "POST", body: JSON.stringify(input) },
    departmentSchema,
  );
}

export function updateDepartment(
  workspaceId: string,
  departmentId: string,
  input: UpdateDepartmentInput,
): Promise<Department> {
  return call(
    apiPaths.departments.detail(workspaceId, departmentId),
    { method: "PATCH", body: JSON.stringify(input) },
    departmentSchema,
  );
}

export function deleteDepartment(
  workspaceId: string,
  departmentId: string,
): Promise<{ deleted: true }> {
  return call(
    apiPaths.departments.detail(workspaceId, departmentId),
    { method: "DELETE" },
    z.object({ deleted: z.literal(true) }),
  );
}

export function restoreDepartment(workspaceId: string, departmentId: string): Promise<Department> {
  return call(
    apiPaths.departments.restore(workspaceId, departmentId),
    { method: "POST" },
    departmentSchema,
  );
}

// -- Imports (CSV / Sheets / Slack) ---------------------------------------

export function csvDryRun(workspaceId: string, csvContent: string): Promise<DryRunResult> {
  return call(
    apiPaths.imports.csv(workspaceId),
    { method: "POST", body: JSON.stringify({ csvContent }) },
    dryRunResultSchema,
  );
}

export function commitImport(
  workspaceId: string,
  importId: string,
  holdInviteEmails: readonly string[] = [],
): Promise<CommitResult> {
  return call(
    apiPaths.imports.commit(workspaceId, importId),
    { method: "POST", body: JSON.stringify({ holdInviteEmails }) },
    commitResultSchema,
  );
}

export function listImports(workspaceId: string): Promise<ImportHistoryList> {
  return call(apiPaths.imports.list(workspaceId), { method: "GET" }, importHistoryListSchema);
}

/** Fetch a Google Sheet as raw CSV so the column-mapping UI can work on it. */
export function fetchSheetCsv(workspaceId: string, sheetsUrl: string): Promise<{ csv: string }> {
  return call(
    apiPaths.imports.sheetFetch(workspaceId),
    { method: "POST", body: JSON.stringify({ sheetsUrl }) },
    z.object({ csv: z.string() }),
  );
}

export function slackSync(
  workspaceId: string,
): Promise<{ importId: string; created: number; updated: number; errors: number; status: string }> {
  return call(
    apiPaths.imports.slackSync(workspaceId),
    { method: "POST", body: JSON.stringify({}) },
    z.object({
      importId: z.string(),
      created: z.number(),
      updated: z.number(),
      errors: z.number(),
      status: z.string(),
    }),
  );
}

// -- Slack OAuth connection (slice 6) ---------------------------------------

export interface SlackConnectionStatus {
  connected: boolean;
  slackTeamId: string | null;
  teamName: string | null;
  connectedAt: string | null;
}

export function getSlackStatus(workspaceId: string): Promise<SlackConnectionStatus> {
  return call(
    apiPaths.integrations.slackStatus(workspaceId),
    { method: "GET" },
    z.object({
      connected: z.boolean(),
      slackTeamId: z.string().nullable(),
      teamName: z.string().nullable(),
      connectedAt: z.string().nullable(),
    }),
  );
}

export function startSlackOauth(workspaceId: string): Promise<{ authUrl: string }> {
  return call(
    apiPaths.integrations.slackOauthStart(workspaceId),
    { method: "POST", body: JSON.stringify({}) },
    z.object({ authUrl: z.string().url() }),
  );
}

export function disconnectSlack(workspaceId: string): Promise<{ ok: true }> {
  return call(
    apiPaths.integrations.slack(workspaceId),
    { method: "DELETE" },
    z.object({ ok: z.literal(true) }),
  );
}

// -- Bulk operations (slices 7 + 8) -----------------------------------------

export function bulkEditHr(
  workspaceId: string,
  input: z.infer<typeof bulkEditHrInputSchema>,
): Promise<BulkActionResult> {
  return call(
    apiPaths.memberships.bulkEditHr(workspaceId),
    { method: "POST", body: JSON.stringify(input) },
    bulkActionResultSchema,
  );
}

export function bulkAssignManager(
  workspaceId: string,
  input: z.infer<typeof bulkAssignManagerInputSchema>,
): Promise<BulkActionResult> {
  return call(
    apiPaths.memberships.bulkAssignManager(workspaceId),
    { method: "POST", body: JSON.stringify(input) },
    bulkActionResultSchema,
  );
}

export function bulkChangeRole(
  workspaceId: string,
  input: z.infer<typeof bulkChangeRoleInputSchema>,
): Promise<BulkActionResult> {
  return call(
    apiPaths.memberships.bulkChangeRole(workspaceId),
    { method: "POST", body: JSON.stringify(input) },
    bulkActionResultSchema,
  );
}

export function bulkRemove(
  workspaceId: string,
  input: z.infer<typeof bulkRemoveInputSchema>,
): Promise<BulkActionResult> {
  return call(
    apiPaths.memberships.bulkRemove(workspaceId),
    { method: "POST", body: JSON.stringify(input) },
    bulkActionResultSchema,
  );
}

export function bulkResendInvitations(
  workspaceId: string,
  input: z.infer<typeof bulkInvitationActionInputSchema>,
): Promise<BulkActionResult> {
  return call(
    apiPaths.invites.bulkResend(workspaceId),
    { method: "POST", body: JSON.stringify(input) },
    bulkActionResultSchema,
  );
}

export function bulkRevokeInvitations(
  workspaceId: string,
  input: z.infer<typeof bulkInvitationActionInputSchema>,
): Promise<BulkActionResult> {
  return call(
    apiPaths.invites.bulkRevoke(workspaceId),
    { method: "POST", body: JSON.stringify(input) },
    bulkActionResultSchema,
  );
}

export function managerBulkEditPrefs(
  workspaceId: string,
  input: z.infer<typeof managerBulkEditPrefsInputSchema>,
): Promise<BulkActionResult> {
  return call(
    apiPaths.memberships.myReportsBulkEditPrefs(workspaceId),
    { method: "POST", body: JSON.stringify(input) },
    bulkActionResultSchema,
  );
}

// -- Slice 4: single-row manager assignment + my-reports list --------------

/** Shore error code for "the chosen manager is still a plain member". */
export const MANAGER_ROLE_REQUIRED_CODE = "MANAGER_ROLE_REQUIRED";

/** Shape of `error.details` on a MANAGER_ROLE_REQUIRED response. */
export interface ManagerRoleRequiredDetails {
  managerMembershipId: string;
  managerDisplayName: string;
  currentRole: string;
}

/**
 * Narrow an unknown thrown value to the manager-role 409, or null. The caller
 * uses the result to offer "Make manager and assign", so anything that isn't
 * that exact code (a cycle CONFLICT, a network error) must fall through to
 * ordinary error handling.
 */
export function readManagerRoleRequiredDetails(error: unknown): ManagerRoleRequiredDetails | null {
  if (!(error instanceof ApiError) || error.code !== MANAGER_ROLE_REQUIRED_CODE) return null;
  const details = error.details;
  if (!details || typeof details !== "object") return null;
  const d = details as Partial<ManagerRoleRequiredDetails>;
  if (typeof d.managerMembershipId !== "string" || typeof d.managerDisplayName !== "string") {
    return null;
  }
  return {
    managerMembershipId: d.managerMembershipId,
    managerDisplayName: d.managerDisplayName,
    currentRole: typeof d.currentRole === "string" ? d.currentRole : "member",
  };
}

export const assignManagerResultSchema = z.object({
  reportMembershipId: z.string(),
  newManagerMembershipId: z.string().nullable(),
});

export type AssignManagerBody =
  | { newManagerMembershipId: string; promoteManagerRole?: true }
  | { unassign: true };

export function assignManager(
  workspaceId: string,
  membershipId: string,
  body: AssignManagerBody,
): Promise<z.infer<typeof assignManagerResultSchema>> {
  return call(
    apiPaths.memberships.manager(workspaceId, membershipId),
    { method: "PATCH", body: JSON.stringify(body) },
    assignManagerResultSchema,
  );
}

export function listMyReports(
  workspaceId: string,
  opts: { includeIndirect?: boolean } = {},
): Promise<MyReportsResponse> {
  const qs = opts.includeIndirect ? "?includeIndirect=true" : "";
  return call(
    `${apiPaths.memberships.myReports(workspaceId)}${qs}`,
    { method: "GET" },
    myReportsResponseSchema,
  );
}
